import { createContext, lazy, Suspense, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
// import Lenis from "lenis"; // REMOVED: static import broke Cloud Build prerender (ERR_REQUIRE_ESM — lenis is ESM-only, can't be require()'d by the CJS-transpiled prerender.js)
import Navbar from "@/components/Navbar";
import { isTouch } from "@/lib/device";
import { FlatMotionContext, useFlatMotionState } from "@/lib/motion";

const Footer = lazy(() => import("@/components/Footer"));
const OrderModal = lazy(() => import("@/components/OrderModal"));
const AIAssistant = lazy(() => import("@/components/AIAssistant"));
const IdeaPanel = lazy(() => import("@/components/IdeaPanel"));

const OrderContext = createContext(() => {});
const IdeaContext = createContext(() => {});
export const useOrder = () => useContext(OrderContext);
export const useIdea = () => useContext(IdeaContext);

export default function SiteLayout({ children }) {
  const { pathname, hash } = useLocation();
  const [orderOpen, setOrderOpen] = useState(false);
  const [ideaOpen, setIdeaOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [deferred, setDeferred] = useState(false);
  const flat = useFlatMotionState();
  const lenisRef = useRef(null);

  const openOrder = useCallback((type) => {
    setPrefill(typeof type === "string" ? type : null);
    setIdeaOpen(false);
    setOrderOpen(true);
  }, []);
  const openIdea = useCallback(() => setIdeaOpen(true), []);

  useEffect(() => {
    const t = setTimeout(() => setDeferred(true), 2500);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (isTouch()) return undefined;
    let raf;
    let cancelled = false;
    // ADDED: dynamic import instead of static top-level import above — keeps lenis out of
    // the server-side prerender bundle entirely, since it only ever runs in the browser here.
    import("lenis").then(({ default: Lenis }) => {
      if (cancelled) return;
      const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
      lenisRef.current = lenis;
      const loop = (time) => {
        lenis.raf(time);
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      lenisRef.current?.destroy();
      lenisRef.current = null;
    };
  }, []);

  const firstRender = useRef(true);
  useEffect(() => {
    const target = hash ? document.getElementById(hash.slice(1)) : null;
    if (target) {
      target.scrollIntoView();
      return;
    }
    // Prerendered pages are scrollable before hydration: never yank the visitor back to the top on mount.
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (lenisRef.current) lenisRef.current.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname, hash]);

  return (
    <OrderContext.Provider value={openOrder}>
      <IdeaContext.Provider value={openIdea}>
      <FlatMotionContext.Provider value={flat}>
      <div className="relative bg-void text-slate-100">
        <div
          className="fixed inset-0 pointer-events-none z-[1] opacity-[0.35]"
          style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)", backgroundSize: "32px 32px" }}
        />
        <div className="relative z-[2]">
          <a href="#main" className="skip-link">Skip to content</a>
          <Navbar onOrder={openOrder} />
          <main id="main">{children}</main>
          <Suspense fallback={null}>
            <Footer onOrder={openOrder} />
          </Suspense>
          <Suspense fallback={null}>
            {(deferred || orderOpen) && (
              <OrderModal open={orderOpen} onClose={() => setOrderOpen(false)} prefillType={prefill} />
            )}
            {(deferred || ideaOpen) && <IdeaPanel open={ideaOpen} onClose={() => setIdeaOpen(false)} />}
            {deferred && <AIAssistant onOrder={openOrder} />}
          </Suspense>
        </div>
      </div>
      </FlatMotionContext.Provider>
      </IdeaContext.Provider>
    </OrderContext.Provider>
  );
}
